import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Clock, DollarSign, Loader2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/config/api';
import { OnlinePaymentStatus, ReadyTextOptIn, placeOnlineOrder, forgetOrder } from '@/components/checkout';
import { orderPath } from '../lib/orderPath';

const PICKUP_LABELS = { asap: 'ASAP (~15 min)', '15': 'In 15 minutes', '30': 'In 30 minutes', '45': 'In 45 minutes', '60': 'In 1 hour' };

/**
 * Online pickup checkout. The server prices the order (menu, promo, tax rate);
 * the customer pays on the restaurant's secure Aster Gateway page in this same
 * tab and comes back to /order/checkout?order=&t=, where this page resumes and
 * polls for the gateway's verdict before showing the confirmation.
 */
export default function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const resumeOrder = searchParams.get('order');
  const resumeToken = searchParams.get('t');
  const returnedCancelled = searchParams.get('cancelled') === '1';
  const { cartItems, restaurantSlug, getCartTotal, getCartCount, clearCart } = useCart();

  // Form state
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    email: '',
  });

  const [smsNotify, setSmsNotify] = useState(false);
  const [pickupTime, setPickupTime] = useState('asap');
  const [tipOption, setTipOption] = useState(null);
  const [customTip, setCustomTip] = useState('');
  const [errors, setErrors] = useState({});
  const [config, setConfig] = useState(null);
  const [placing, setPlacing] = useState(false);
  const resuming = !!(resumeOrder && resumeToken);

  // Redirect if cart is empty (not while an order's payment is on screen)
  useEffect(() => {
    if (cartItems.length === 0 && !resuming) {
      toast.error('Your cart is empty');
      navigate(orderPath('cart'));
    }
  }, [cartItems, navigate, resuming]);

  useEffect(() => {
    if (!restaurantSlug || resuming) return;
    axios.get(`${API}/online/${restaurantSlug}/checkout-config`)
      .then(res => {
        setConfig(res.data);
        const presets = res.data.tip_presets || [];
        setTipOption(presets.length ? String(presets[Math.min(1, presets.length - 1)]) : 'none');
      })
      .catch(() => setConfig({ accepting_orders: false, reasons: ['unavailable'] }));
  }, [restaurantSlug, resuming]);

  // The cart at menu prices; the server prices the order (promo, tax) when it is placed.
  const subtotal = getCartTotal();
  const tipsEnabled = config?.tips_enabled !== false;
  const tipPresets = (config?.tip_presets || []).map(String);

  const calculateTip = () => {
    if (!tipsEnabled || !tipOption || tipOption === 'none') return 0;
    if (tipOption === 'custom') return Math.max(0, parseFloat(customTip) || 0);
    return Math.round(subtotal * (parseFloat(tipOption) / 100) * 100) / 100;
  };

  const tip = calculateTip();

  // Validation
  const validateForm = () => {
    const newErrors = {};

    if (!customerInfo.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!customerInfo.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(customerInfo.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (customerInfo.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerInfo.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (tipOption === 'custom' && customTip && parseFloat(customTip) < 0) {
      newErrors.tip = 'Tip amount cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePlaceOrder = async () => {
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setPlacing(true);
    try {
      const answer = await placeOnlineOrder(restaurantSlug, {
        items: cartItems.map(item => ({
          menu_item_id: item.id,
          quantity: item.quantity,
          modifier_option_ids: (item.modifiers || []).flatMap(group => (group.options || []).map(opt => opt.id)),
          notes: item.specialInstructions || null,
        })),
        customer_name: customerInfo.name.trim(),
        customer_phone: customerInfo.phone.trim(),
        customer_email: customerInfo.email.trim() || null,
        pickup_time: pickupTime,
        tip_cents: Math.round(tip * 100),
        sms_notify: smsNotify,
      });
      if (answer.payment_status === 'paid') {
        handlePaid({ order_id: answer.order_id }, answer.order_id, answer.access_token);
        return;
      }
      // the order and its page are in the URL: a reload or the return from the page resumes here
      setSearchParams({ order: String(answer.order_id), t: answer.access_token }, { replace: true });
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not place your order. Please try again.');
    } finally {
      setPlacing(false);
    }
  };

  const handlePaid = (_payment, orderId = resumeOrder, token = resumeToken) => {
    clearCart();
    forgetOrder();
    toast.success('Payment received — your order is in!');
    navigate(orderPath(`confirmation/${orderId}?t=${token}`), { replace: true });
  };

  const getPickupTimeLabel = () => PICKUP_LABELS[pickupTime] || pickupTime;

  if (resuming) {
    return (
      <OnlinePaymentStatus
        orderId={resumeOrder}
        accessToken={resumeToken}
        cancelledReturn={returnedCancelled}
        onPaid={handlePaid}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(orderPath('cart'))}
              className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5" />
              <span className="font-medium">Back to Cart</span>
            </button>
            <h1 className="text-xl font-bold text-gray-900">Checkout</h1>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Order Type Badge */}
        <div className="flex items-center justify-center">
          <Badge className="bg-blue-100 text-blue-700 text-base px-4 py-2">
            🚶 Pickup Order
          </Badge>
        </div>

        {/* Customer Info */}
        <Card>
          <CardContent className="p-4">
            <h2 className="font-semibold text-lg text-gray-900 mb-4">Contact Information</h2>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-sm font-medium">
                  Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  placeholder="John Doe"
                  value={customerInfo.name}
                  onChange={(e) => {
                    setCustomerInfo({ ...customerInfo, name: e.target.value });
                    setErrors({ ...errors, name: '' });
                  }}
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && (
                  <p className="text-sm text-red-600 mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <Label htmlFor="phone" className="text-sm font-medium">
                  Phone Number <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={customerInfo.phone}
                  onChange={(e) => {
                    setCustomerInfo({ ...customerInfo, phone: e.target.value });
                    setErrors({ ...errors, phone: '' });
                  }}
                  className={errors.phone ? 'border-red-500' : ''}
                />
                {errors.phone && (
                  <p className="text-sm text-red-600 mt-1">{errors.phone}</p>
                )}
                <div className="mt-3">
                  <ReadyTextOptIn checked={smsNotify} onChange={setSmsNotify} />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-sm font-medium">
                  Email (Optional)
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john@example.com"
                  value={customerInfo.email}
                  onChange={(e) => {
                    setCustomerInfo({ ...customerInfo, email: e.target.value });
                    setErrors({ ...errors, email: '' });
                  }}
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && (
                  <p className="text-sm text-red-600 mt-1">{errors.email}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pickup Time */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="h-5 w-5 text-gray-600" />
              <h2 className="font-semibold text-lg text-gray-900">Pickup Time</h2>
            </div>
            
            <RadioGroup value={pickupTime} onValueChange={setPickupTime}>
              <div className="space-y-2">
                {(config?.pickup_options || ['asap', '15', '30']).map((option) => (
                  <div
                    key={option}
                    className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer ${
                      pickupTime === option ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                    }`}
                    onClick={() => setPickupTime(option)}
                    data-testid={`pickup-${option}`}
                  >
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value={option} id={`pickup-${option}`} />
                      <Label htmlFor={`pickup-${option}`} className="cursor-pointer font-medium">
                        {PICKUP_LABELS[option] || option}
                      </Label>
                    </div>
                  </div>
                ))}
              </div>
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Tip Selection */}
        {tipsEnabled && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="h-5 w-5 text-gray-600" />
              <h2 className="font-semibold text-lg text-gray-900">Add a Tip</h2>
            </div>

            <div className="grid gap-2 mb-3" style={{ gridTemplateColumns: `repeat(${tipPresets.length + 1}, minmax(0, 1fr))` }}>
              {tipPresets.map((pct) => (
                <Button
                  key={pct}
                  variant={tipOption === pct ? 'default' : 'outline'}
                  onClick={() => { setTipOption(pct); setErrors({ ...errors, tip: '' }); }}
                  className="h-12"
                  data-testid={`tip-${pct}`}
                >
                  {pct}%
                </Button>
              ))}
              <Button
                variant={tipOption === 'none' ? 'default' : 'outline'}
                onClick={() => setTipOption('none')}
                className="h-12"
                data-testid="tip-none"
              >
                No Tip
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant={tipOption === 'custom' ? 'default' : 'outline'}
                onClick={() => setTipOption('custom')}
                className="flex-shrink-0"
              >
                Custom
              </Button>
              {tipOption === 'custom' && (
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={customTip}
                  onChange={(e) => {
                    setCustomTip(e.target.value);
                    setErrors({ ...errors, tip: '' });
                  }}
                  className={`${errors.tip ? 'border-red-500' : ''}`}
                />
              )}
            </div>
            {errors.tip && (
              <p className="text-sm text-red-600 mt-2">{errors.tip}</p>
            )}
          </CardContent>
        </Card>
        )}

        {/* Order Summary */}
        <Card>
          <CardContent className="p-4">
            <h2 className="font-semibold text-lg text-gray-900 mb-4">Order Summary</h2>
            
            {/* Items */}
            <div className="space-y-3 mb-4 pb-4 border-b">
              {cartItems.map((item) => (
                <div key={item.cartItemId} className="flex justify-between text-sm">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">
                      {item.quantity}x {item.name}
                    </div>
                    {item.modifiers && item.modifiers.length > 0 && (
                      <div className="text-xs text-gray-500 mt-1">
                        {item.modifiers.map((mod, idx) => (
                          <div key={idx}>
                            {mod.options.map(opt => opt.name).join(', ')}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="font-medium text-gray-900">
                    ${item.totalPrice.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="space-y-2">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal ({getCartCount()} items)</span>
                <span className="font-medium">${subtotal.toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between text-gray-600">
                <span>Tax</span>
                <span className="text-sm" data-testid="tax-at-checkout">Calculated at checkout</span>
              </div>

              {tip > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>Tip</span>
                  <span className="font-medium">${tip.toFixed(2)}</span>
                </div>
              )}

              <p className="text-xs text-gray-500 pt-2">
                The restaurant prices your order (menu, promo, sales tax) when you place it; the payment page shows the exact total.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sticky Place Order Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50">
        <div className="max-w-2xl mx-auto px-4 py-4">
          {config && !config.accepting_orders && (
            <p className="text-sm text-center text-red-600 mb-2" data-testid="not-accepting">
              {config.reasons?.includes('closed') ? 'The restaurant is closed right now.' : 'Online ordering is not available right now.'}
            </p>
          )}
          <Button
            onClick={handlePlaceOrder}
            disabled={placing || !config?.accepting_orders}
            className="w-full h-14 text-lg font-semibold bg-blue-600 hover:bg-blue-700"
            data-testid="place-order-btn"
          >
            {placing ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Lock className="h-4 w-4 mr-2" />Place order &amp; pay</>}
          </Button>
          <p className="text-xs text-center text-gray-500 mt-2">
            Pickup {getPickupTimeLabel()}
          </p>
        </div>
      </div>

    </div>
  );
}
